/**
 * Tabbed carousel: lazily builds a Swiper instance per tab, only when that
 * tab is first shown. component-tabs.js already owns tab switching
 * (aria-selected/hidden toggling) — this file only listens for the same
 * clicks and layers Swiper init on top, so a hidden panel is never handed
 * to Swiper (a hidden/display:none container makes Swiper miscalculate
 * slide widths). Once built, a tab's Swiper instance and its loaded images
 * persist in the DOM for the life of the page — switching away and back
 * never re-instantiates or re-fetches anything.
 */
// Feature 322: the theme editor can run this file again when it re-renders the
// section; the guard keeps the class and the listeners below from being declared twice.
if (!window.nativeTabbedCarouselLoaded) {
window.nativeTabbedCarouselLoaded = true;

class TabbedCarousel {
  constructor(root) {
    this.root = root;
    this.swipers = new Map(); // panel id -> Swiper instance

    const panels = this.root.querySelectorAll('.tab-content');
    if (panels.length) this.initSwiperForPanel(panels[0]);

    this.root.querySelectorAll('.tab-button').forEach((btn) => {
      btn.addEventListener('click', () => {
        const panelId = btn.getAttribute('aria-controls');
        const panel = this.root.querySelector(`#${CSS.escape(panelId)}`);
        if (panel) this.initSwiperForPanel(panel);
      });
    });
  }

  // Feature 322: one document-level listener (see bottom of file) calls this, so
  // reloaded sections do not pile up window listeners.
  onBlockSelect(target) {
    const slide = target.classList && target.classList.contains('tabbed-carousel-card')
      ? target
      : target.closest && target.closest('.tabbed-carousel-card');
    const panel = target.closest ? target.closest('.tab-content') : null;

    if (panel && panel.hasAttribute('hidden')) {
      const tabButton = this.root.querySelector(`[aria-controls="${panel.id}"]`);
      if (tabButton) tabButton.click(); // triggers component-tabs.js's changeTabs() + our init hook above
    }
    if (panel) this.initSwiperForPanel(panel);

    if (slide) {
      const swiper = this.swipers.get(panel ? panel.id : null);
      if (swiper) {
        const index = +slide.dataset.index || 0;
        swiper.slideTo(index, 300);
      }
    }
  }

  initSwiperForPanel(panel) {
    if (!panel || this.swipers.has(panel.id)) return;

    const container = panel.querySelector('.tabbed-carousel-swiper');
    if (!container) return;

    const loop = container.dataset.loop === 'true';
    const swiper = new Swiper(container, {
      slidesPerView: 'auto',
      spaceBetween: 0, // gutter is CSS margin-right on .swiper-slide (--tcc-gutter), kept in one place
      loop: loop,
      watchOverflow: true,
      navigation: {
        nextEl: container.querySelector('.swiper-button-next'),
        prevEl: container.querySelector('.swiper-button-prev'),
      },
    });
    this.swipers.set(panel.id, swiper);

    container.querySelectorAll('img').forEach((img) => {
      if (img.complete) {
        img.classList.add('is-loaded');
      } else {
        img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
      }
    });
  }
}

// swiper.js now loads via assets/load-asset-once-style snippet insertion
// (createElement, not document.write, to avoid Lighthouse's document.write
// flag) — defer/async don't apply to a dynamically-inserted script, so it
// can genuinely still be loading when DOMContentLoaded fires. The
// constructor below calls `new Swiper(...)` immediately for the first tab
// panel, so wait for window.Swiper to actually exist rather than assume it
// does by this point.
function whenSwiperReady(callback) {
  if (window.Swiper) {
    callback();
    return;
  }
  const check = setInterval(() => {
    if (window.Swiper) {
      clearInterval(check);
      callback();
    }
  }, 20);
}

// Feature 322: the theme editor swaps in new section HTML without firing
// DOMContentLoaded again, which left reloaded carousels with no Swiper and every
// image stuck at opacity 0 behind the loading shimmer. Build any carousel that
// has no instance yet, on page load and again on every section load.
const tabbedCarousels = new WeakMap(); // .tab-grand root -> TabbedCarousel

function bootTabbedCarousels(scope) {
  whenSwiperReady(() => {
    scope.querySelectorAll('.section-tabbed-carousel .tab-grand').forEach((root) => {
      if (!tabbedCarousels.has(root)) tabbedCarousels.set(root, new TabbedCarousel(root));
    });
  });
}

document.addEventListener('DOMContentLoaded', () => bootTabbedCarousels(document));
document.addEventListener('shopify:section:load', (event) => bootTabbedCarousels(event.target));

document.addEventListener('shopify:block:select', (event) => {
  const root = event.target.closest && event.target.closest('.tab-grand');
  const instance = root && tabbedCarousels.get(root);
  if (instance) instance.onBlockSelect(event.target);
});

}
